import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import './OrgSignupPage.css';

// Inline Google brand mark — using the official multi-color "G" per Google's
// brand guidelines (don't recolor or restyle).
function GoogleG({ size = 18 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}

export default function LoginPage() {
  const { signIn, session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Where to land after sign-in: the route that bounced us (state), or the
  // ?from= the expired-session redirect carried. Same-origin paths only.
  const fromParam = searchParams.get('from') || '';
  const from = location.state?.from
    ? `${location.state.from.pathname}${location.state.from.search || ''}` // keep ?open=… from texted links
    : (fromParam.startsWith('/') && !fromParam.startsWith('//') ? fromParam : '/app');

  const [email, setEmail] = useState(() => searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(() => {
    if (searchParams.get('expired')) return 'Your session expired — please sign in again.';
    // Set by useSessionTimeout right before it signs this browser out.
    try {
      if (sessionStorage.getItem('pi-portal-idle-logout')) {
        sessionStorage.removeItem('pi-portal-idle-logout');
        return 'You were signed out after 15 minutes without activity. Sign in to pick up where you left off.';
      }
    } catch {}
    return '';
  });
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && session) navigate(from, { replace: true });
  }, [session, authLoading, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { error: err } = await signIn(email, password);
      if (err) {
        setError(err.message === 'Invalid login credentials' ? 'Invalid email or password.' : err.message);
        setLoading(false); return;
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(''); setOauthLoading(true);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // After Google auth + Supabase callback, return user to the path
        // they were originally trying to reach.
        redirectTo: `${window.location.origin}${from}`,
      },
    });
    if (err) { setError(err.message); setOauthLoading(false); }
    // On success the browser is redirected to Google — no further code runs here.
  };

  return (
    <div className="sup">
      {/* Centered logo above hero — no top bar on login */}
      <div style={{ paddingTop: 56, display: 'flex', justifyContent: 'center' }}>
        <Link to="/login" aria-label="MediLink home">
          <img src="/logo-vertical.svg" alt="MediLink" style={{ height: 56, width: 'auto', display: 'block' }} />
        </Link>
      </div>

      {/* Hero */}
      <div className="sup-hero" style={{ paddingTop: 32 }}>
        <h1>Welcome <em>back</em>.</h1>
        <p>Sign in to continue managing your referrals, scheduling, and team.</p>
      </div>

      {/* Card */}
      <div className="sup-card-wrap" style={{ maxWidth: 480 }}>
        <div className="sup-card">
          {error && <div className="sup-error">{error}</div>}

          {/* OAuth button — primary visual placement */}
          <button
            type="button"
            onClick={handleGoogle}
            disabled={oauthLoading || loading}
            className="lp-oauth"
          >
            <GoogleG size={18} />
            {oauthLoading ? 'Redirecting to Google…' : 'Continue with Google'}
          </button>

          {/* Divider */}
          <div className="lp-divider"><span>or sign in with email</span></div>

          <form onSubmit={handleSubmit} className="sup-form">
            <div>
              <label className="sup-field-label">Email</label>
              <input
                type="email"
                className="sup-input"
                placeholder="you@practice.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div>
              <label className="sup-field-label">Password</label>
              <input
                type="password"
                className="sup-input"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -4 }}>
              <Link to="/forgot-password" style={{ fontSize: 12, color: '#0da7ca', textDecoration: 'none', fontWeight: 500 }}>
                Forgot password?
              </Link>
            </div>
            <div className="sup-actions">
              <button type="submit" disabled={loading || oauthLoading} className="sup-btn sup-btn-primary">
                {loading ? 'Signing in…' : 'Sign in'} <ArrowRight size={14} />
              </button>
            </div>
          </form>
        </div>

        <p className="sup-fineprint" style={{ marginTop: 24 }}>
          Don't have an account?{' '}
          <Link to="/register" style={{ color: '#0da7ca', fontWeight: 600, textDecoration: 'none' }}>Register here</Link>
        </p>
      </div>

      {/* Login-specific styles — appended to the .sup CSS surface */}
      <style>{`
        .lp-oauth {
          width: 100%;
          padding: 12px 18px;
          border-radius: 12px;
          background: white;
          border: 1px solid rgba(11,11,20,0.10);
          font-family: inherit;
          font-size: 14px;
          font-weight: 600;
          color: #0b0b14;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          transition: all 0.2s ease;
        }
        .lp-oauth:hover:not(:disabled) {
          border-color: #0b0b14;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -4px rgba(11,11,20,0.15);
        }
        .lp-oauth:disabled { opacity: 0.6; cursor: not-allowed; }
        .lp-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 20px 0;
          color: rgba(11,11,20,0.45);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .lp-divider::before, .lp-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(11,11,20,0.08);
        }
      `}</style>
    </div>
  );
}
